'use client'

// Composant navigateur : importer depuis `impact-dimensions` (types et
// couleurs, sans données) et non depuis `impact-scoring`, qui embarquerait
// project-scores.json (1 Mo) dans le paquet du navigateur.
import type { ImpactScoreResult } from '@/data/impact-dimensions'
import { DIMENSION_COLORS } from '@/data/impact-dimensions'

export interface ImpactRadarProps {
  result: ImpactScoreResult
  size?: number
  showLabels?: boolean
  showLegend?: boolean
  className?: string
}

/**
 * Radar pentagonal des 5 dimensions d'impact, sans contexte de traduction.
 *
 * Séparé de `ImpactRadar` pour l'espace contributeur : `useI18n` importe les
 * dictionnaires du site (≈ 125 Ko) qui partiraient sinon dans le paquet de la
 * fiche projet de l'espace. La langue arrive ici en propriété ; `ImpactRadar`
 * (site public) la lit dans le contexte et la transmet.
 *
 * Les textes du SVG sont stylés en attributs (et non en classes Tailwind),
 * pour un rendu identique sur le site public et dans l'espace, qui ne charge
 * pas Tailwind. Valeurs reprises des classes d'origine.
 */
export function ImpactRadarSvg({
  result,
  size = 280,
  showLabels = true,
  showLegend = false,
  className = '',
  locale = 'fr',
}: ImpactRadarProps & { locale?: string }) {
  const padding = showLabels ? 50 : 10
  const totalSize = size + padding * 2
  const cx = totalSize / 2
  const cy = totalSize / 2
  const radius = size * 0.35
  const labelRadius = size * 0.48

  const n = 5 // 5 dimensions
  const angleStep = (2 * Math.PI) / n
  const startAngle = -Math.PI / 2 // Start from top

  // Calculate points for a given array of values (0-100)
  const getPoints = (values: number[]) =>
    values.map((v, i) => {
      const angle = startAngle + i * angleStep
      const r = (v / 100) * radius
      return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) }
    })

  // Grid levels
  const gridLevels = [20, 40, 60, 80, 100]
  const values = result.radarData.map((d) => d.value)
  const dataPoints = getPoints(values)
  const dataPath = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z'

  // Label positions
  const labelPositions = result.radarData.map((_, i) => {
    const angle = startAngle + i * angleStep
    return { x: cx + labelRadius * Math.cos(angle), y: cy + labelRadius * Math.sin(angle) }
  })

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <svg width="100%" viewBox={`0 0 ${totalSize} ${totalSize}`} className="drop-shadow-sm">
        {/* Grid lines */}
        {gridLevels.map((level) => {
          const pts = getPoints(Array(n).fill(level))
          const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z'
          return (
            <path
              key={level}
              d={path}
              fill="none"
              stroke="#e5e7eb"
              strokeWidth={level === 60 ? 1.5 : 0.5}
              strokeDasharray={level === 60 ? '4 2' : undefined}
              opacity={level === 60 ? 0.8 : 0.4}
            />
          )
        })}

        {/* Axis lines */}
        {Array.from({ length: n }).map((_, i) => {
          const angle = startAngle + i * angleStep
          const x2 = cx + radius * Math.cos(angle)
          const y2 = cy + radius * Math.sin(angle)
          return <line key={i} x1={cx} y1={cy} x2={x2} y2={y2} stroke="#d1d5db" strokeWidth="0.5" />
        })}

        {/* Data area */}
        <path d={dataPath} fill="#1B4D3E" fillOpacity="0.15" stroke="#1B4D3E" strokeWidth="2" />

        {/* Data points */}
        {dataPoints.map((p, i) => {
          const key = result.radarData[i]!.key
          const isHighlight = result.highlights.includes(key)
          return (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r={isHighlight ? 5 : 3.5}
              fill={DIMENSION_COLORS[key]}
              stroke="white"
              strokeWidth="2"
            />
          )
        })}

        {/* Labels */}
        {showLabels &&
          labelPositions.map((pos, i) => {
            const d = result.radarData[i]!
            const label = locale === 'en' ? d.labelEn : d.label
            const anchor =
              Math.abs(pos.x - cx) < 5 ? 'middle' : pos.x > cx ? 'start' : 'end'
            const dy = pos.y < cy - radius * 0.5 ? -4 : pos.y > cy + radius * 0.5 ? 12 : 4
            return (
              <g key={i}>
                <text
                  x={pos.x}
                  y={pos.y + dy}
                  textAnchor={anchor}
                  style={{ fontFamily: 'Arial, sans-serif', fontSize: 10, fontWeight: 600, fill: '#4b5563' }}
                >
                  {label}
                </text>
                <text
                  x={pos.x}
                  y={pos.y + dy + 13}
                  textAnchor={anchor}
                  style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, fontWeight: 700, fill: DIMENSION_COLORS[d.key] }}
                >
                  {d.value}%
                </text>
              </g>
            )
          })}

        {/* Center score */}
        <text x={cx} y={cy - 6} textAnchor="middle" style={{ fontFamily: 'Arial, sans-serif', fontSize: 22, fontWeight: 700, fill: '#1B4D3E' }}>
          {result.average}
        </text>
        <text x={cx} y={cy + 10} textAnchor="middle" style={{ fontFamily: 'Arial, sans-serif', fontSize: 9, fill: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {locale === 'en' ? 'score' : 'score'}
        </text>
      </svg>

      {/* Optional legend */}
      {showLegend && (
        <div className="flex flex-wrap gap-3 mt-4 justify-center">
          {result.radarData.map((d) => (
            <div key={d.key} className="flex items-center gap-1.5 text-xs text-gray-600">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: DIMENSION_COLORS[d.key] }} />
              <span>{locale === 'en' ? d.labelEn : d.label}</span>
              <span className="font-bold" style={{ color: DIMENSION_COLORS[d.key] }}>{d.value}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
